import React, {useEffect, useState} from "react";
import {Button, Form, message, Modal, Popconfirm, Row, Select, Space, Table, InputNumber, Input} from "antd";
import KHExtensionsService, {KHExtensionAny, KHExtensionType} from "../../services/KHExtensionsService";
import ace from "brace";
import JsonInput from "../../components/FormComponents/JsonInput";

type KHExtensionModel = Omit<KHExtensionAny, "id"> & {
    id?: string;
    private_data?: any;
    version?: number;
    description?: string;
}

interface KHExtensionModalProps {
    isVisible: boolean;
    onOk: (values: any) => void;
    onCancel: () => void;
    initialValues?: KHExtensionModel | null;
}

const KHExtensionModal: React.FC<KHExtensionModalProps> = ({ isVisible, onOk, onCancel, initialValues }) => {
    const [form] = Form.useForm();

    const handleTypeChange = (type: KHExtensionType) => {
        if (type === 'json') {
            form.setFieldsValue({ data: {} });
        }
    };

    const handleFinish = (values: any) => {
        onOk({ ...values, id: initialValues?.id });
    };

    useEffect(() => {
        if (isVisible) {
            form.resetFields();
            if (initialValues) {
                form.setFieldsValue({
                    type: initialValues.type,
                    scope: initialValues.scope,
                    description: initialValues.description,
                    data: typeof initialValues.data === 'string'
                        ? JSON.parse(`"${initialValues.data}"`)
                        : initialValues.data,
                    private_data: typeof initialValues.private_data === 'string'
                        ? JSON.parse(`"${initialValues.private_data}"`)
                        : initialValues.private_data,
                    version: initialValues.version
                });
            } else {
                form.setFieldsValue({
                    type: 'json',
                    scope: 'hierarchy-custom-bulk-update-template',
                    description: '',
                    data: {},
                    private_data: {},
                    version: 1
                });
            }
        }
    }, [isVisible, initialValues]);

    const layout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 16 },
    };

    return (
        <Modal
            title={initialValues ? "Edit extension" : "New extension"}
            visible={isVisible}
            destroyOnClose={true}
            onOk={() => form.submit()}
            onCancel={onCancel}
            width="80vw"
            bodyStyle={{ height: '65vh', overflow: "scroll" }}
        >
            <Form
                {...layout}
                form={form}
                name="new-extension-form"
                onFinish={handleFinish}
            >
                <Form.Item
                    label="Type"
                    name="type"
                    rules={[{ required: true }]}
                    wrapperCol={{ span: 8 }}
                >
                    <Select onChange={handleTypeChange}>
                        <Select.Option value="json">Json</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    label="Scope"
                    name="scope"
                    rules={[{ required: true }]}
                    wrapperCol={{ span: 8 }}
                >
                    <Select>
                        <Select.Option value="hierarchy-custom-bulk-update-template">Template: Custom Bulk Update</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    label="Description"
                    name="description"
                    wrapperCol={{ span: 12 }}
                >
                    <Input.TextArea 
                        rows={3}
                        placeholder="Description"
                    />
                </Form.Item>
                <Form.Item
                    label="Version"
                    name="version"
                    rules={[{ required: true, type: 'number', min: 1, message: 'Version must be a number greater than or equal to 1' }]}
                    wrapperCol={{ span: 8 }}
                >
                    <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    label="Data"
                    name="data"
                    rules={[
                        {
                            validator: (_, value) => {
                                if (value === undefined) {
                                    return Promise.reject('JSON must be valid');
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                    wrapperCol={{ span: 12 }}
                >
                    <JsonInput
                        value={form.getFieldValue('data')}
                        onChange={(value: any) => {
                            form.setFieldsValue({ data: value });
                        }}
                        onInvalidValue={(onChange: (value: any) => void) => onChange(undefined)}
                        options={{
                            mode: 'code',
                            ace: ace,
                            htmlElementProps: { 
                                style: { height: '40vh' },
                            }
                        }}
                    />
                </Form.Item>
                <Form.Item
                    label="Private Data"
                    name="private_data"
                    rules={[
                        {
                            validator: (_, value) => {
                                if (value === undefined) {
                                    return Promise.reject('JSON must be valid');
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                    wrapperCol={{ span: 12 }}
                >
                    <JsonInput
                        value={form.getFieldValue('private_data')}
                        onChange={(value: any) => {
                            form.setFieldsValue({ private_data: value });
                        }}
                        onInvalidValue={(onChange: (value: any) => void) => onChange(undefined)}
                        options={{
                            mode: 'code',
                            ace: ace,
                            htmlElementProps: {
                                style: { height: '20vh' },
                            }
                        }}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

interface KHExtensionsSettingsProps {
    match: {
        params: {
            workspaceId: string;
        }
    }
}

const KHExtensionsSettings: React.FC<KHExtensionsSettingsProps> = ({ match }) => {
    const [extensionsList, setExtensionsList] = useState<KHExtensionAny[]>([]);
    const [extensionModalVisible, setExtensionModalVisible] = useState(false);
    const [currentExtension, setCurrentExtension] = useState<KHExtensionModel | null>(null);

    const loadExtensionsList = async () => {
        try {
            const result = await KHExtensionsService.fetchAllExtensions(match.params.workspaceId);
            setExtensionsList(result);
        } catch {
            message.error('Failed to load extensions list');
        }
    };

    const handleAddExtension = () => {
        setCurrentExtension({
            type: 'json',
            scope: 'hierarchy-custom-bulk-update-template',
            description: '',
            data: {},
            private_data: {},
            version: 1
        });
        setExtensionModalVisible(true);
    };

    const handleSaveExtension = async (values: any) => {
        const data = values.data;

        const extension: KHExtensionModel = {
            type: values.type,
            scope: values.scope,
            description: values.description,
            data: JSON.stringify(data),
            private_data: JSON.stringify(values.private_data),
            version: values.version
        };

        try {
            if (values.id) {
                const form = {
                    ...extension,
                    id: values.id,
                }
                await KHExtensionsService.updateExtension(match.params.workspaceId, form);
                message.success('Extension updated');
            } else {
                await KHExtensionsService.addExtension(match.params.workspaceId, extension);
                message.success('Extension added');
            }
            await loadExtensionsList();
            setExtensionModalVisible(false);
        } catch {
            message.error('Failed to add extension');
        }
    };

    const handleDeleteExtension = async (workspaceId: string, extensionId: string) => {
        try {
            await KHExtensionsService.deleteExtension(workspaceId, extensionId);
            message.success('Extension removed');
            await loadExtensionsList();
        } catch {
            message.error('Failed to remove extension');
        }
    };

    const handleEditExtension = async (workspaceId: string, extensionId: string) => {
        try {
            const res = await KHExtensionsService.fetchExtensionById(workspaceId, extensionId);
            setCurrentExtension({
                id: res.id,
                type: res.type,
                scope: res.scope,
                description: res.description,
                data: typeof res.data === 'string' ? JSON.parse(`"${res.data}"`) : res.data,
                private_data: typeof res.private_data === 'string' ? JSON.parse(`"${res.private_data}"`) : res.private_data,
                version: res.version
            });
            setExtensionModalVisible(true);
        } catch {
            message.error('Failed to load extension');
        }
    };

    useEffect(() => {
        loadExtensionsList();
    }, []);

    const extensionsListColumns = [
        {
            title: 'Type',
            dataIndex: 'type',
        },
        {
            title: 'Scope',
            dataIndex: 'scope',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            render: (description: string) => description || '-'
        },
        {
            title: 'Version',
            dataIndex: 'version',
        },
        {
            title: 'Actions',
            dataIndex: 'id',
            render: (id: string) => (
                <Space>
                    <a href="#" onClick={() => handleEditExtension(match.params.workspaceId, id)}>Edit</a>
                    <Popconfirm
                        title="Are you sure to delete the extension？"
                        onConfirm={() => handleDeleteExtension(match.params.workspaceId, id)}
                    >
                        <a href="#">Delete</a>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Row style={{ justifyContent: 'flex-end' }}>
                <Button type="primary" onClick={handleAddExtension}>Add extension</Button>
            </Row>
            <Table
                id="extensions-list"
                dataSource={extensionsList}
                columns={extensionsListColumns}
                size="small"
                style={{ paddingTop: '16px' }}
            />
            <KHExtensionModal
                isVisible={extensionModalVisible}
                onOk={handleSaveExtension}
                onCancel={() => setExtensionModalVisible(false)}
                initialValues={currentExtension}
            />
        </div>
    );
};

export default KHExtensionsSettings;
